import urlcat from 'urlcat';
import type { AxiosRequestConfig } from 'axios';

import type {
    AllEntities,
    DisplayMediaBreakdownData,
    EndpointMap,
    Displays_OperationalTime,
    Displays_Vendor,
    FrameSearchScreens,
    HttpProgressUpdate,
    ListSearchOptions,
    ListSearchResponse,
    ModuleConstructorProps,
} from '../Types';
import { generateArrayQSParams } from '../Lib';

type ListEndpointArgs = {
    endpoint: string;
    options?: ListSearchOptions;
};

type EntityOperation = keyof EndpointMap['Entities'][keyof AllEntities];

/** The entity's URL for `operation`; throws (rather than requesting `.../undefined`) when the
 * endpoint map has none, e.g. the local-only `SpotlightRecentResource`. */
export const entityEndpoint = (
    endpoints: EndpointMap,
    entityName: keyof AllEntities,
    operation: EntityOperation,
): string => {
    const endpoint = endpoints.Entities[entityName][operation];
    if (!endpoint) {
        throw new Error(`Entity '${entityName}' has no '${operation}' endpoint`);
    }
    return endpoint;
};

export function BaseEntityModuleFuncs<Entity>(
    { endpoints, httpClient, baseUrl }: ModuleConstructorProps,
    entityName: keyof AllEntities,
) {
    if (process.env.NODE_ENV === 'development') {
        (['list', 'get', 'create', 'update'] as const).forEach(operation => {
            if (!endpoints.Entities[entityName][operation]) {
                // eslint-disable-next-line no-console -- dev-only warning for a gap in the endpoint map
                console.warn(
                    `Entity '${entityName}' does not contain an endpoint for '${operation}'`,
                );
            }
        });
    }

    return {
        list: (options: ListSearchOptions = {}) =>
            ListEndpointFunc<ListSearchResponse<Entity>>(httpClient)({
                endpoint: entityEndpoint(endpoints, entityName, 'list'),
                options,
            }),
        cursor: (options: ListSearchOptions = {}) =>
            ListEndpointFunc<ListSearchResponse<Entity>>(httpClient)({
                endpoint: entityEndpoint(endpoints, entityName, 'cursor'),
                options,
            }),
        create: BaseCreateFunc<Entity>({ endpoints, httpClient, baseUrl }, entityName),
        get: (
            id: number,
            includes?: string[],
            showIncludes?: boolean,
            config?: AxiosRequestConfig,
        ) => {
            const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
            return httpClient.get<{ data: Entity }>(
                `${urlcat(entityEndpoint(endpoints, entityName, 'get'), {
                    id,
                    showIncludes,
                })}${includeParams}`,
                config,
            );
        },
        update: (
            id: number,
            entity: Partial<Entity>,
            includes?: string[],
            config?: AxiosRequestConfig,
        ) => {
            const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
            return httpClient.put<{ data: Entity }>(
                `${urlcat(entityEndpoint(endpoints, entityName, 'update'), {
                    id,
                })}${includeParams}`,
                entity,
                config,
            );
        },
        delete: (id: number, config?: AxiosRequestConfig) =>
            httpClient.delete(
                urlcat(entityEndpoint(endpoints, entityName, 'delete'), { id }),
                null,
                config,
            ),
    };
}

export const BaseCreateFunc =
    <Entity>(
        { endpoints, httpClient, baseUrl }: ModuleConstructorProps,
        entityName: keyof AllEntities,
    ) =>
    (entity: Partial<Entity>, includes?: string[], config?: AxiosRequestConfig) => {
        const includeParams = generateArrayQSParams(includes ?? [], 'include', '?');
        return httpClient.post<{ data: Entity }>(
            `${entityEndpoint(endpoints, entityName, 'create')}${includeParams}`,
            entity,
            config,
        );
    };

export const ListEndpointFunc =
    <ResponseType>(client: ModuleConstructorProps['httpClient']) =>
    ({ endpoint, options = {} }: ListEndpointArgs, config?: AxiosRequestConfig) => {
        const {
            searchables = {},
            page = 1,
            perPage = 25,
            filters = {},
            includes = [],
            showFilters,
            showSearchables,
            filterFilters,
            showIncludes,
            orderBy,
            direction,
            cursor,
            smartFilters = false,
        } = options;

        const scopes = generateArrayQSParams(
            Array.isArray(filters?.scope) ? filters.scope : [],
            'scope',
            '&',
        );
        return client.post<ResponseType>(
            `${urlcat(endpoint, {
                page,
                perPage,
                orderBy,
                direction,
                cursor,
                showFilters,
                showSearchables,
                showIncludes,
                filterFilters: smartFilters,

                ...searchables,
            })}${scopes}`,
            {
                ...searchables,
                ...filters,
                page,
                perPage,
                filterFilters,
                orderBy,
                direction,
                cursor,
                include: includes,
            },
            config,
        );
    };
export const operationalHoursFuncs = <Entity>(
    props: ModuleConstructorProps,
    createEndpoint: string,
    deleteEndpoint: string,
) => ({
    createOperationalHours: (
        id: number,
        operationalTimes: Partial<Displays_OperationalTime>[],
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.post<{ data: Entity }>(
            `${urlcat(createEndpoint, {
                id,
            })}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            { operational_timings: operationalTimes },
            config,
        ),
    deleteOperationalHours: (
        id: number,
        operationalHourId: number,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.delete<{ data: Entity }>(
            `${urlcat(deleteEndpoint, {
                id,
                oid: operationalHourId,
            })}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            null,
            config,
        ),
});
export const brochureFuncs = <Entity>(
    props: ModuleConstructorProps,
    createEndpoint: string,
    deleteEndpoint: string,
) => ({
    createBrochure: (
        id: number,
        formData: FormData,
        onProgress?: HttpProgressUpdate,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.postFile<{ data: Entity }>(
            `${urlcat(createEndpoint, {
                id,
            })}${generateArrayQSParams(includes ?? [], 'include', '?')}`,

            formData,
            onProgress,
            {},
            config,
        ),
    deleteBrochure: (id: number, includes?: string[], config?: AxiosRequestConfig) =>
        props.httpClient.delete<{ data: Entity }>(
            `${urlcat(deleteEndpoint, {
                id,
            })}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            null,
            config,
        ),
});
export const environmentFuncs = (props: ModuleConstructorProps, listEndpoint: string) => ({
    listEnvironments: (config?: AxiosRequestConfig) =>
        props.httpClient.get<{ data: string[] }>(`${listEndpoint}`, config),
});
export const attachVendorIdsFuncs = <Entity>(
    props: ModuleConstructorProps,
    createEndpoint: string,
    deleteEndpoint: string,
    listEndpoint: string,
) => ({
    attachVendors: (
        id: number,
        vendors: Partial<Displays_Vendor>[],
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.post<{ data: Entity }>(
            `${urlcat(createEndpoint, {
                id,
            })}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            { vendors },
            config,
        ),
    deleteVendors: (screenId: number, vendorId: number, config?: AxiosRequestConfig) =>
        props.httpClient.delete<{ data: Entity }>(
            `${urlcat(deleteEndpoint, { screenId, vendorId })}`,
            {},
            config,
        ),
    listVendorIDs: (config?: AxiosRequestConfig) => props.httpClient.post(`${listEndpoint}`),
});
export const uploadManualAudienceFunc = <Entity>(
    props: ModuleConstructorProps,
    createEndpoint: string,
) => ({
    uploadManualAudience: (
        formData: FormData,
        onProgress?: HttpProgressUpdate,
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.postFile<{ data: Entity }>(
            `${urlcat(createEndpoint, {})}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            formData,
            onProgress,
            {},
            config,
        ),
});

export const frameSearchfunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    frameSearch: (
        data: { market_id: number; frames: { vendor_id: string }[]; engine?: string },
        includes?: string[],
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.post<FrameSearchScreens>(
            `${urlcat(createEndpoint, {})}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            data,
            config,
        ),
});
export const exportCsvfunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    csvExport: (
        data: {
            [key: `search.${string}`]: string | undefined;
            filter?: any;
        },
        config?: AxiosRequestConfig,
        includes?: string[],
    ) =>
        props.httpClient.post(
            `${urlcat(createEndpoint, {})}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            data,
            config,
        ),
});
export const reImportFunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    reImport: (
        data: {
            [key: `search.${string}`]: string | undefined;
            filter?: any;
        },
        config?: AxiosRequestConfig,
        includes?: string[],
    ) =>
        props.httpClient.post(
            `${urlcat(createEndpoint, {})}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            data,
            config,
        ),
});
export const massUpdatingFunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    massUpdate: (formData: FormData, onProgress?: HttpProgressUpdate) =>
        props.httpClient.postFile(`${urlcat(createEndpoint, {})}`, formData, onProgress),
});
export const screenOrOwnerUpdateFunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    screenOrOwnerUpdate: (id: number) => props.httpClient.post(`${urlcat(createEndpoint, {})}`, id),
});
export const screenAllocationfunc = (props: ModuleConstructorProps, createEndpoint: string) => ({
    allocateAndDisAllocate: (
        data: {
            allocation_type: 'allocate' | 'deallocate';
            screen_to_allocate_type: 'selected' | 'filtered';
            filter?: any;
            display_ids?: number[];
            allocation_to_type: 'packs' | 'tags';
            allocation_to_type_id?: number;
            allow_override?: boolean;
        },
        config?: AxiosRequestConfig,
        includes?: string[],
    ) =>
        props.httpClient.post(
            `${urlcat(createEndpoint, {})}${generateArrayQSParams(includes ?? [], 'include', '?')}`,
            data,
            config,
        ),
});

export const addChannelFrameFunc = (
    props: ModuleConstructorProps,
    addChannelFrameEndpoint: string,
    removeChannelFrameEndpoint: string,
) => ({
    addChannelFrame: (id, data: { channel_frame_ids: number[] }, config?: AxiosRequestConfig) =>
        props.httpClient.post(`${urlcat(addChannelFrameEndpoint, { id })}`, data, config),

    removeChannelFrame: (id, data: { channel_frame_ids: number[] }, config?: AxiosRequestConfig) =>
        props.httpClient.post(`${urlcat(removeChannelFrameEndpoint, { id })}`, data, config),
});

export const mediaBreakdownFunc = (props: ModuleConstructorProps, endpoint: string) => ({
    displayMediaBreakdown: (
        displayId: number,
        data: { start_date?: Date; end_date?: Date; group_by: string[] },
        config?: AxiosRequestConfig,
    ) =>
        props.httpClient.post<{ data: DisplayMediaBreakdownData[] }>(
            urlcat(endpoint, {
                id: displayId,
            }),
            { start_date: data.start_date, end_date: data.end_date, group_by: data.group_by },
            config,
        ),
});
